import * as React from "react";
import { InputIconLoupe } from "@mebi/ui";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface SearchInputProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "size"
> {
  size?: "sm" | "md" | "lg" | "mobile";
  containerClassName?: string;
  onClear?: () => void;
  iconClassName?: string;
}

const sizeClasses: Record<NonNullable<SearchInputProps["size"]>, string> = {
  sm: "h-9 text-sm px-3",
  md: "h-10 text-sm px-3",
  lg: "h-11 text-base px-4",
  mobile: "h-12 text-base px-4",
};

const SearchInput = React.forwardRef<HTMLInputElement, SearchInputProps>(
  (
    {
      className,
      containerClassName,
      size = "md",
      value,
      disabled,
      onClear,
      iconClassName,
      ...props
    },
    ref,
  ) => {
    const hasValue = typeof value === "string" && value.length > 0;

    return (
      <div className={cn("relative w-full", containerClassName)}>
        <div
          className={cn(
            "absolute inset-y-0 left-3 flex items-center pointer-events-none",
            iconClassName || "text-Color-Icon-default",
          )}
        >
          <InputIconLoupe className="h-4 w-4" />
        </div>

        <input
          ref={ref}
          type="text"
          value={value}
          disabled={disabled}
          className={cn(
            "w-full rounded-lg border bg-Color-Background-neutral-default_primary text-Color-Text-neutral-black placeholder:text-Color-Text-neutral-supplement focus-visible:outline-none focus-visible:border-Color-Stroke-brand focus-visible:ring-2 focus-visible:ring-Color-Stroke-brand/20 disabled:cursor-not-allowed disabled:opacity-50",
            sizeClasses[size],
            "pl-9 pr-9",
            className,
          )}
          {...props}
        />

        {hasValue && (
          <button
            type="button"
            onClick={onClear}
            disabled={disabled}
            className="absolute inset-y-0 right-2 flex items-center text-Color-Icon-default disabled:cursor-not-allowed"
            aria-label="Xóa tìm kiếm"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
    );
  },
);

SearchInput.displayName = "SearchInput";

export { SearchInput };
